import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { ErrorService } from '@shared/services/error.service';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/fr';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { UtilisService } from '../../services/utilis.service';
import { ShowWeekCountHeaderComponent } from '../date-picker-show-week/show-week-count-header/show-week-count-header.component';
import { MY_FORMATS } from './data-format.const';

dayjs.extend(utc);
dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);
dayjs.locale('fr');

@Component({
  selector: 'app-range-date-picker',
  templateUrl: './range-date-picker.component.html',
  styleUrls: ['./range-date-picker.component.scss'],
  standalone: false,
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class RangeDatePickerComponent implements OnInit, OnChanges {
  private instanceId: string;

  public localeTime: {
    firstDay: number;
    startDate: Dayjs;
    endDate: Dayjs;
    format: string;
    applyLabel: string;
    cancelLabel: string;
    daysOfWeek: string[];
    monthNames: string[];
    customRangeLabel: string;
    weekLabel: string;
    fromLabel: string;
    toLabel: string;
    startKey: string;
    endKey: string;
  } = {
    firstDay: 1,
    startDate: dayjs().startOf('day'),
    endDate: dayjs().endOf('day'),
    format: 'DD/MM/YYYY',
    applyLabel: 'Appliquer',
    cancelLabel: 'Annuler',
    customRangeLabel: 'Plage personnalisée',
    daysOfWeek: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
    monthNames: [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ],
    weekLabel: 'S',
    fromLabel: 'De',
    toLabel: 'À',
    startKey: 'startDate',
    endKey: 'endDate',
  };

  public ranges: any = {};

  public defaultRanges: any = {
    "Aujourd'hui": [dayjs().startOf('day'), dayjs().startOf('day')],
    'Ce mois-ci': [dayjs().startOf('month'), dayjs().endOf('month')],
  };

  public pastRanges: any = {
    Hier: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').startOf('day')],
    // 'Les 7 derniers jours': [dayjs().subtract(6, 'days').startOf('day'), dayjs().startOf('day')],
    // 'Les 30 derniers jours': [dayjs().subtract(29, 'days').startOf('day'), dayjs().startOf('day')],
    'Semaine dernière': [dayjs().subtract(1, 'week').startOf('isoWeek'), dayjs().subtract(1, 'week').endOf('isoWeek')],
    'Le mois dernier': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
    'Les 6 derniers mois': [dayjs().subtract(6, 'month'), dayjs()],
    'Les 12 derniers mois': [dayjs().subtract(12, 'month'), dayjs()],
  };

  public futurRanges: any = {
    'Année en cours': [dayjs().startOf('year'), dayjs().endOf('year')],
    'Trimestre en cours': [dayjs().startOf('quarter'), dayjs().endOf('quarter')],
    S1: [dayjs().startOf('year'), dayjs().month(5).endOf('month')],
    S2: [dayjs().month(6).startOf('month'), dayjs().endOf('year')],
  };

  public weekRanges: any = {
    'cette semaine-ci': [dayjs().startOf('isoWeek'), dayjs().endOf('isoWeek')],
    'La semaine prochaine': [dayjs().add(1, 'week').startOf('isoWeek'), dayjs().add(1, 'week').endOf('isoWeek')],
    'les 2 semaines prochaines': [dayjs().add(1, 'week').startOf('isoWeek'), dayjs().add(2, 'week').endOf('isoWeek')],
    'les 4 semaines prochaines': [dayjs().add(1, 'week').startOf('isoWeek'), dayjs().add(4, 'week').endOf('isoWeek')],
    'les 12 semaines prochaines': [dayjs().add(1, 'week').startOf('isoWeek'), dayjs().add(12, 'week').endOf('isoWeek')],
    'les 24 semaines prochaines': [dayjs().add(1, 'week').startOf('isoWeek'), dayjs().add(24, 'week').endOf('isoWeek')],
  };

  @ViewChild('picker') public picker!: MatDateRangePicker<Date>;

  @ViewChild('dp', { static: false }) public dp: any;

  @ViewChild('dateInput') public dateInput!: ElementRef;

  @ViewChild('datePickerContainer', { static: false }) private datePickerContainer!: ElementRef;

  // @ViewChild('fusedDateInput', { static: false }) private fusedDateInput!: ElementRef;

  @Output() public dateSwitch = new EventEmitter<string>();

  @Output() public SelectedDates = new EventEmitter<{ startDate: Date; endDate: Date }>();

  @Output() public updateAgendas = new EventEmitter<boolean>();

  @Input() public startDay: moment.Moment | string | Date | undefined;

  @Input() public endDay: moment.Moment | string | Date | undefined;

  @Input() public showArrows: boolean = true;

  @Input() public isDisableWeek: boolean = false;

  @Input() public isDisablePicker: boolean = false;

  @Input() public isThereNewModifs: boolean = false;

  @Input() public isVerifyModifs: boolean = false;

  @Input() public sideOpening: string = '';

  @Input() public minDate: Date | undefined = undefined;

  @Input() public maxDate: Date | undefined = undefined;

  @Input() public isTodayMinDate: boolean | undefined = undefined;

  @Input() public hasDefaultRanges: boolean = false;

  @Input() public hasPastRanges: boolean = false;

  @Input() public hasFuturRanges: boolean = false;

  @Input() public showRanges: boolean | undefined = true;

  @Input() public refreshDateSubject: Subject<void> = new Subject<void>();

  @Input() public hasWeekRanges: boolean | undefined = false;

  @Input() public onlyWeeks: boolean | undefined = false;

  @Input() public fusionnedInput: boolean = false;

  public header = ShowWeekCountHeaderComponent;

  public formatedMinDate: dayjs.Dayjs | undefined = undefined;

  public formatedMaxDate: dayjs.Dayjs | undefined = undefined;

  public dropsVar: 'up' | 'down' = 'down';

  public calendarIsOpen: boolean = false;

  public showDatePicker: boolean = true;

  // public isDateRangeOverflowing: boolean = false;

  public formattedDateRange: string = '';

  // public displayedDateRange: string = '';

  constructor(
    private utilisService: UtilisService,
    private errorService: ErrorService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['day'] && changes['day'].previousValue) {
      this.initStartDateFromDay();
    }
    if (
      (changes['startDay'] && changes['startDay'].previousValue) ||
      (changes['endDay'] && changes['endDay'].previousValue)
    ) {
      this.initStartDateFromDay();
    }
    if (changes['minDate'] || changes['maxDate']) {
      this.setMinDate();

      this.showDatePicker = false;
      setTimeout(() => (this.showDatePicker = true), 1000);
    }
  }

  ngOnInit() {
    this.setMinDate();
    this.initStartDateFromDay();

    this.ranges = {
      ...(this.hasDefaultRanges ? (this.defaultRanges ?? {}) : {}),
      ...(this.hasPastRanges ? (this.pastRanges ?? {}) : {}),
      ...(this.hasFuturRanges ? (this.futurRanges ?? {}) : {}),
      ...(this.hasWeekRanges ? (this.weekRanges ?? {}) : {}),
    };

    this.subscriptionManager.createRequest(this.refreshDateSubject, 'refreshDate', this.instanceId).subscribe({
      next: () => {
        this.initStartDateFromDay();
      },

      error: (error: any) => {
        this.errorService.handleError(error);
      },
    });
  }

  public selected: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  } = {
    startDate: null,
    endDate: null,
  };

  public locale = {
    applyLabel: 'Appliquer',
    cancelLabel: 'Annuler',
    customRangeLabel: 'Plage personnalisée',
    daysOfWeek: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
    monthNames: [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ],
    firstDay: 1, // Lundi comme premier jour de la semaine
    format: 'DD/MM/YYYY',
    weekLabel: 'S',
  };

  triggerPicker(): void {
    const position = this.detectInputPosition();
    this.calendarIsOpen = true;

    this.dropsVar = position === 'top' ? 'up' : 'down';
    if (this.dateInput) {
      setTimeout(() => {
        this.dateInput.nativeElement.click();
      });
    }
  }

  initStartDateFromDay(): void {
    this.selected.startDate = this.startDay ? dayjs(this.startDay.toString()) : null;
    this.selected.endDate = this.endDay ? dayjs(this.endDay.toString()) : null;
    this.updateFormattedDateRange();
  }

  dayIsSelected(event: any) {
    if (!event.startDate || !event.endDate) {
      this.calendarIsOpen = false;
      return;
    }

    const start = dayjs(event.startDate);
    const end = dayjs(event.endDate);

    this.selected.startDate = this.onlyWeeks ? start.startOf('isoWeek') : start;
    this.selected.endDate = this.onlyWeeks ? end.endOf('isoWeek') : end;

    this.updateFormattedDateRange();

    this.SelectedDates.emit({
      startDate: this.selected.startDate.startOf('day').utc().toDate(),
      endDate: this.selected.endDate.startOf('day').utc().toDate(),
    });

    this.calendarIsOpen = false;
  }

  closePicker() {
    this.calendarIsOpen = false;
  }

  goToNextEvent() {
    if (this.isThereNewModifs) {
      return this.utilisService.openUnSavedChangesPopup();
    } else {
      return new Observable((sub: any) => sub.next(true));
    }
  }

  setMinDate() {
    if (this.isTodayMinDate) {
      this.formatedMinDate = dayjs(new Date().toString());
    } else {
      this.formatedMinDate = this.minDate ? dayjs(this.minDate.toString()) : undefined;
    }
    this.formatedMaxDate = this.maxDate ? dayjs(this.maxDate.toString()) : undefined;
  }

  private detectInputPosition(): 'top' | 'bottom' {
    if (!this.datePickerContainer) {
      return 'bottom';
    }

    const element = this.datePickerContainer.nativeElement;
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const elementCenter = rect.top + rect.height / 2;
    const windowCenter = windowHeight / 2;

    if (elementCenter < windowCenter) {
      return 'bottom';
    } else {
      return 'top';
    }
  }

  formatFusedDateRange(): string {
    if (!this.selected.startDate || !this.selected.endDate) {
      return '';
    }
    return `${this.selected.startDate.format('DD MMMM YYYY')} - ${this.selected.endDate.format('DD MMMM YYYY')}`;
  }

  private updateFormattedDateRange(): void {
    this.formattedDateRange = this.formatFusedDateRange();
    // setTimeout(() => this.checkDateRangeOverflow(), 0);
  }

  // private checkDateRangeOverflow(): void {
  //   if (this.fusedDateInput) {
  //     const element = this.fusedDateInput.nativeElement;
  //     this.isDateRangeOverflowing = element.scrollWidth > element.clientWidth;
  //   }
  // }
}
